export class OrderSuccessModal {
  constructor(config = null) {
    this.config = config;
    this.isVisible = false;
    this.orderData = null;
    this.displayItems = [];
  }

  show(orderData) {
    if (this.isVisible) {
      return;
    }

    this.orderData = orderData;
    this.displayItems = this.prepareDisplayItems(orderData);
    this.isVisible = true;

    this.render();
    this.bindEvents();
    this.animateIn();

    document.body.style.overflow = 'hidden';
  }

  hide() {
    this.isVisible = false;
    const modal = document.getElementById('order-success-modal');
    if (modal) {
      modal.remove();
    }

    if (this.boundHandleKeyDown) {
      document.removeEventListener('keydown', this.boundHandleKeyDown);
    }
    if (this.boundHandleDocumentClick) {
      document.removeEventListener('click', this.boundHandleDocumentClick);
    }

    document.body.style.overflow = '';
  }

  getCurrency() {
    if (this.orderData?.currency) {
      return this.orderData.currency;
    }
    if (this.orderData?.items?.length > 0) {
      const firstItemCurrency = this.orderData.items.find(item => item.currency);
      if (firstItemCurrency?.currency) {
        return firstItemCurrency.currency;
      }
    }
    return this.config?.settings?.currency || this.config?.currency || 'PLN';
  }

  render() {
    const currency = this.getCurrency();
    const modalHtml = `
      <div id="order-success-modal" class="order-success-minimal">
        <div class="success-overlay"></div>
        <div class="success-container">
          <!-- Success Icon Circle -->
          <div class="success-circle">
            <i class="fas fa-check"></i>
            <span class="circle-ring"></span>
          </div>

          <!-- Title -->
          <h1 class="success-title">Order Confirmed!</h1>
          <p class="success-subtitle">Your order has been received</p>

          <!-- Order Details -->
          <div class="order-details-minimal">
            <div class="divider-line"></div>

            <!-- Order Items -->
            <div class="order-items-list">
              ${this.renderOrderItems(currency)}
            </div>

            <!-- Total -->
            <div class="order-total-row">
              <span class="total-label">Total</span>
              <span class="total-value">${currency} ${this.resolveTotal().toFixed(2)}</span>
            </div>
            <div class="receipt-info" style="margin-top:12px; font-size:13px; color: var(--color-text-secondary);">
              <div>Paper fiscal receipt is available at the counter.</div>
              ${this.orderData?.digitalReceipt?.consent && (this.orderData?.digitalReceipt?.email || this.orderData?.digitalReceipt?.phone) ? `
                <div>Digital receipt will be sent to ${this.orderData.digitalReceipt.email || this.orderData.digitalReceipt.phone}.</div>
              ` : ''}
            </div>

            <!-- Order Number -->
            ${this.orderData.orderNumber ? `
              <div class="order-number-section">
                <span class="order-number-label">Order #${this.orderData.orderNumber}</span>
              </div>
            ` : ''}
          </div>

          <!-- Close Button -->
          <button class="close-button-minimal" id="success-close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
  }

  renderOrderItems(currency) {
    return this.displayItems.map(item => `
      <div class="order-item-row">
        <div class="item-info">
          <span class="item-quantity">${item.quantity}</span>
          <span class="item-name">${item.name}</span>
        </div>
        <span class="item-price">${currency} ${(item.totalPrice).toFixed(2)}</span>
      </div>
    `).join('');
  }

  resolveTotal() {
    if (this.orderData?.total != null && !Number.isNaN(Number(this.orderData.total))) {
      return Number(this.orderData.total);
    }
    return this.displayItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
  }

  prepareDisplayItems(orderData = {}) {
    const sourceItems = Array.isArray(orderData.items) ? orderData.items : [];
    const grouped = new Map();

    sourceItems.forEach(item => {
      const key = this.buildGroupingKey(item);
      const existing = grouped.get(key);
      const quantity = this.getItemQuantity(item);
      const unitPrice = this.resolveItemUnitPrice(item);

      if (existing) {
        existing.quantity += quantity;
        existing.totalPrice += unitPrice * quantity;
      } else {
        grouped.set(key, {
          id: item.id,
          name: item.name,
          quantity,
          totalPrice: unitPrice * quantity,
          unitPrice,
          currency: item.currency || orderData.currency || this.config?.currency || 'PLN',
          modifiers: item.modifiers || item.selectedModifiers || [],
          specialInstructions: this.normalizeSpecialInstructions(item.specialInstructions)
        });
      }
    });

    return Array.from(grouped.values());
  }

  buildGroupingKey(item = {}) {
    const modifiersKey = this.serializeModifiers(item.modifiers || item.selectedModifiers || []);
    const specialInstructions = this.normalizeSpecialInstructions(item.specialInstructions);
    return `${item.id || item.menuItemId || 'unknown'}|${modifiersKey}|${specialInstructions}`;
  }

  getItemQuantity(item = {}) {
    if (typeof item.quantity === 'number' && item.quantity > 0) {
      return item.quantity;
    }
    return 1;
  }

  resolveItemUnitPrice(item = {}) {
    if (typeof item.totalPrice === 'number' && typeof item.quantity === 'number' && item.quantity > 0) {
      return item.totalPrice / item.quantity;
    }
    if (typeof item.price === 'number') {
      return item.price;
    }
    if (typeof item.unitPrice === 'number') {
      return item.unitPrice;
    }
    if (item.price && typeof item.price.amount === 'number') {
      return item.price.amount;
    }
    if (item.unitPrice && typeof item.unitPrice.amount === 'number') {
      return item.unitPrice.amount;
    }
    return 0;
  }

  serializeModifiers(modifiers) {
    if (!modifiers) {
      return '[]';
    }
    if (Array.isArray(modifiers)) {
      const normalized = modifiers.map(mod => {
        if (mod && typeof mod === 'object') {
          const group = mod.groupId || mod.group || mod.category || mod.id || mod.name || '';
          const option = mod.optionId || mod.value || mod.id || mod.name || '';
          return { group, option };
        }
        return { value: mod };
      }).sort((a, b) => {
        const aKey = `${a.group || ''}:${a.option || a.value || ''}`;
        const bKey = `${b.group || ''}:${b.option || b.value || ''}`;
        return aKey.localeCompare(bKey);
      });
      return JSON.stringify(normalized);
    }
    if (typeof modifiers === 'object') {
      const entries = Object.entries(modifiers).map(([group, optionValue]) => {
        if (optionValue && typeof optionValue === 'object') {
          const optionId = optionValue.id || optionValue.value || optionValue.name || optionValue;
          return { group, option: optionId };
        }
        return { group, option: optionValue };
      }).sort((a, b) => {
        const aKey = `${a.group || ''}:${a.option || ''}`;
        const bKey = `${b.group || ''}:${b.option || ''}`;
        return aKey.localeCompare(bKey);
      });
      return JSON.stringify(entries);
    }
    return JSON.stringify(modifiers);
  }

  normalizeSpecialInstructions(value) {
    return (value || '').trim();
  }

  animateIn() {
    setTimeout(() => {
      const container = document.querySelector('.success-container');
      const circle = document.querySelector('.success-circle');
      const elements = document.querySelectorAll('.success-title, .success-subtitle, .order-details-minimal');

      if (container) {
        container.classList.add('animate-in');
      }

      if (circle) {
        circle.classList.add('animate-scale');
      }

      elements.forEach((el, index) => {
        setTimeout(() => {
          el.classList.add('fade-in');
        }, 100 * (index + 1));
      });
    }, 50);
  }

  bindEvents() {
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);

    document.addEventListener('keydown', this.boundHandleKeyDown);
    document.addEventListener('click', this.boundHandleDocumentClick);
  }

  handleKeyDown(e) {
    if (this.isVisible && e.key === 'Escape') {
      this.hide();
    }
  }

  handleDocumentClick(e) {
    if (!this.isVisible) return;

    if (e.target.id === 'success-close-btn' ||
        e.target.closest('#success-close-btn') ||
        e.target.classList.contains('success-overlay')) {
      e.preventDefault();
      e.stopPropagation();
      this.hide();
    }
  }
}
